import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { cn } from '@lib/utils';
import { MODULLER, type ModulKey } from '@lib/modules';
import { useModulAyarlari } from '@features/ayarlar';
import { useAuthStore } from '@/store/auth';

// Sidebar ile aynı izin eşlemesi (talepler menüsü = "talep" resource).
const MODUL_IZIN: Record<ModulKey, string | null> = {
  dashboard: null,
  musteri: 'musteri:goruntule',
  aktivite: 'aktivite:goruntule',
  firsat: 'firsat:goruntule',
  teklif: 'teklif:goruntule',
  siparis: 'siparis:goruntule',
  urun: 'urun:goruntule',
  destek: 'talep:goruntule',
  eposta: 'eposta:goruntule',
  meta: 'meta:goruntule',
  takvim: 'takvim:goruntule',
};

interface Props {
  /** Tam menüyü (off-canvas drawer) aç. */
  onMenuClick: () => void;
}

/**
 * Mobilde ekranın altında sabit navigasyon çubuğu (md altı).
 * İlk 4 aktif modülü + "Menü" düğmesini gösterir.
 */
export function MobileTabBar({ onMenuClick }: Props) {
  const { t } = useTranslation('common');
  const { data: ayarlar } = useModulAyarlari();
  const hasIzin = useAuthStore((s) => s.hasIzin);

  const aktifModuller = MODULLER
    .map((m) => {
      const a = ayarlar?.find((x) => x.modul === m.key);
      return { ...m, aktif: a?.aktif ?? true, sira: a?.sira ?? MODULLER.indexOf(m) };
    })
    .filter((m) => m.zorunlu || m.aktif)
    .filter((m) => {
      const izin = MODUL_IZIN[m.key];
      return !izin || hasIzin(izin);
    })
    .sort((a, b) => a.sira - b.sira)
    .slice(0, 4);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-stretch border-t bg-card md:hidden"
      aria-label="Mobil gezinme"
    >
      {aktifModuller.map(({ to, i18n, icon: Icon, label, key }) => (
        <NavLink
          key={key}
          to={to}
          className={({ isActive }) =>
            cn(
              'flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
            )
          }
        >
          <Icon className="h-5 w-5" />
          <span className="max-w-full truncate px-0.5">{t(i18n, { defaultValue: label })}</span>
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMenuClick}
        className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        <Menu className="h-5 w-5" />
        <span>Menü</span>
      </button>
    </nav>
  );
}
